import * as React from 'react';
import { useState, useEffect } from 'react';
import { getUsers, addUser, updateUser, deleteUser, getUserGroups } from '@api';
import { FaAlignLeft } from 'react-icons/fa';

function formatLastLogin(val:any){ if(!val) return 'never'; if(typeof val==='string' && val.startsWith('0001')) return 'never'; let d:Date|null=null; if(val instanceof Date) d=val; else if(typeof val==='string'){ const parsed=new Date(val); if(!isNaN(parsed.getTime())) d=parsed;} if(!d) return 'never'; if(d.getFullYear()===1) return 'never'; return d.toLocaleString(undefined,{year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit'}); }

const emptyForm = { userName: '', userFirstName: '', userLastName: '', userAccountEnabled: false, userIsDomain: false, departments: '', userGroups: [] as number[] };

export default function UserAdmin() {
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState<boolean>(false);

  useEffect(() => { fetchUsers(); fetchGroups(); }, []);
  async function fetchUsers() { setLoading(true); setError(null); try { setUsers(await getUsers()); } catch (e:any) { setError(e.message);} finally { setLoading(false);} }
  async function fetchGroups() { try { setGroups(await getUserGroups()); } catch { } }

  const selectedUser = users.find(u => u.id === selectedId);
  function handleSelect(id:number) {
    setIsAdding(false);
    setSelectedId(id);
    const u = users.find(u=>u.id===id);
    setForm({
      userName:u?.userName||'',
      userFirstName:u?.userFirstName||'',
      userLastName:u?.userLastName||'',
      userAccountEnabled:u?.userAccountEnabled||false,
      userIsDomain:u?.userIsDomain||false,
      departments:u?.departments?u.departments.join(','):'',
      userGroups:u?.userGroups? (u.userGroups as any[]).map(g=>Number(g)) : []
    });
  }
  function handleAdd(){ setSelectedId(null); setForm(emptyForm); setIsAdding(true); }
  async function handleDelete(){ if(selectedId===null) return; if(!window.confirm('Delete this user?')) return; setLoading(true); setError(null); try{ await deleteUser(selectedId); await fetchUsers(); setSelectedId(null); setIsAdding(false);}catch(e:any){ setError(e.message);} finally { setLoading(false);} }

  function handleFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) { const target = e.target as HTMLInputElement | HTMLSelectElement; const name = target.name as keyof typeof form; if(target instanceof HTMLInputElement && target.type==='checkbox'){ setForm(prev=>({...prev,[name]:target.checked})); } else if(target instanceof HTMLSelectElement && target.multiple){ const selectedValues = Array.from(target.options).filter(o=>o.selected).map(o=>Number(o.value)); setForm(prev=>({...prev,[name]:selectedValues})); } else { setForm(prev=>({...prev,[name]:(target as any).value})); } }
  function toggleGroup(groupId:number){ setForm(prev=>({...prev, userGroups: prev.userGroups.includes(Number(groupId))? prev.userGroups.filter(id=>id!==Number(groupId)): [...prev.userGroups, Number(groupId)]})); }

  async function handleSave(){ setLoading(true); setError(null); try { const payload = { userName:form.userName, userFirstName:form.userFirstName, userLastName:form.userLastName, userAccountEnabled:form.userAccountEnabled, userIsDomain:form.userIsDomain, departments: form.departments? form.departments.split(',').map(d=>d.trim()).filter(Boolean):[], userGroups: form.userGroups } as any; if(selectedId===null){ const nu = await addUser(payload); await fetchUsers(); setSelectedId(nu.id);} else { await updateUser(selectedId, payload); await fetchUsers(); } setIsAdding(false); } catch(e:any){ setError(e.message);} finally { setLoading(false);} }

  function handleCancel(){
    if(selectedUser){
      setIsAdding(false);
      setForm({ userName:selectedUser.userName, userFirstName:selectedUser.userFirstName, userLastName:selectedUser.userLastName, userAccountEnabled:selectedUser.userAccountEnabled, userIsDomain:selectedUser.userIsDomain, departments:selectedUser.departments?selectedUser.departments.join(','):'', userGroups:selectedUser.userGroups? (selectedUser.userGroups as any[]).map(g=>Number(g)) : [] });
    } else {
      setIsAdding(false);
      setSelectedId(null);
      setForm(emptyForm);
    }
  }

  const inputStyle: React.CSSProperties = { padding:'4px', fontSize:'1em', borderRadius:4, border:'1px solid #cfd8dc', background:'#f7fafd', width:'100%' };
  const buttonPrimary: React.CSSProperties = { padding:'8px 16px', borderRadius:6, background:'#4e6e8e', color:'#fff', fontWeight:600, border:'none', cursor:'pointer' };
  const buttonSecondary: React.CSSProperties = { ...buttonPrimary, background:'#e0e6ed', color:'#123', fontWeight:500 };
  const buttonDanger: React.CSSProperties = { ...buttonPrimary, background:'#c62828' };

  const sortedGroups = React.useMemo(() => {
    return [...groups].sort((a, b) => (a.groupName || '').localeCompare(b.groupName || '', undefined, { sensitivity: 'base' }));
  }, [groups]);

  return (
    <div style={{ display: 'flex', height:'calc(90vh / var(--ui-scale))', border:'1px solid #ccc', borderRadius:12, overflow:'hidden' }}>
      <div style={{ width:420, borderRight:'1px solid #ccc', display:'flex', flexDirection:'column', background:'#f7fafd' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:'1px solid #e0e6ed' }}>
          <h3 style={{ margin:0, fontWeight:600 }}>Users</h3>
          <button onClick={handleAdd} title='Add user' style={{ background:'#4e6e8e', border:'none', width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/></svg>
          </button>
        </div>
        {loading && <div style={{ padding:'8px 16px' }}>Loading...</div>}
        {error && <div style={{ padding:'8px 16px', color:'red' }}>{error}</div>}
        <div style={{ overflowY:'auto', flex:1 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:14 }}>
            <thead>
              <tr style={{ background:'#eaf1f7' }}>
                <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>User</th>
                <th style={{ textAlign:'left', padding:'6px 10px', borderBottom:'1px solid #e0e6ed' }}>Name</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u=>{ 
                const selected=u.id===selectedId; 
                const never=isNeverLoggedIn(u.userLastLogin); 
                const textStyle: React.CSSProperties = { 
                  fontWeight: u.userIsDomain ? 700 : 400, 
                  textDecoration: u.userAccountEnabled ? 'none' : 'line-through', 
                  color: never ? '#888' : undefined 
                }; 
                return (
                  <tr key={u.id} onClick={()=>handleSelect(u.id)} style={{ cursor:'pointer', background:selected?'#d8ecff':'transparent' }}>
                    <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7', ...textStyle }}>{u.userName}</td>
                    <td style={{ padding:'6px 10px', borderBottom:'1px solid #f0f4f7', ...textStyle }}>{(u.userFirstName||'')+' '+(u.userLastName||'')}</td>
                  </tr>
                ); 
              })}
              {users.length===0 && !loading && (<tr><td colSpan={2} style={{ padding:16, textAlign:'center', color:'#888' }}>No users</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'16px 24px', position:'relative' }}>
        <div style={{ maxWidth:700 }}>
          {(!isAdding && selectedId === null) ? (
            <div style={{ color:'#567', fontSize:16, marginBottom:16 }}>
              Select a user from the list or click the + icon to add a new user
            </div>
          ) : ( <div>
<h3 style={{ marginTop:0 }}>{isAdding || selectedId===null? 'Add User':'Edit User'}</h3>            <form onSubmit={e=>{ e.preventDefault(); handleSave(); }} style={{ display:'grid', gridTemplateColumns:'160px 1fr', rowGap:12, columnGap:16, background:'#fff', padding:20, borderRadius:12, border:'1px solid #e0e6ed', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', position:'relative' }}>
              <label style={{ alignSelf:'center', fontWeight:500 }}>User</label><input name='userName' value={form.userName} onChange={handleFormChange} required style={inputStyle} />
              <label style={{ alignSelf:'center', fontWeight:500 }}>First Name</label><input name='userFirstName' value={form.userFirstName} onChange={handleFormChange} required style={inputStyle} />
              <label style={{ alignSelf:'center', fontWeight:500 }}>Last Name</label><input name='userLastName' value={form.userLastName} onChange={handleFormChange} required style={inputStyle} />
              <label style={{ alignSelf:'center', fontWeight:500 }}>Departments</label><input name='departments' value={form.departments} onChange={handleFormChange} placeholder='comma separated' style={inputStyle} />
              <label style={{ alignSelf:'center', fontWeight:500 }}>Group membership</label>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {sortedGroups.map(g=> {
                  const gid = Number(g.id);
                  const isChecked = form.userGroups.includes(gid);
                  return (
                    <label key={g.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:14, background: isChecked ? '#eaf1f7' : 'transparent', borderRadius:4, padding:'2px 6px' }}>
                      <input
                        type='checkbox'
                        checked={isChecked}
                        onChange={()=>toggleGroup(gid)}
                      />
                      {g.groupName}
                    </label>
                  );
                })}
                {sortedGroups.length===0 && <div style={{ color:'#888', padding:8 }}>No groups</div>}
              </div>
              <label style={{ alignSelf:'center', fontWeight:500, gridColumn:'1 / span 1', textAlign:'left' }}>Account Enabled</label>
              <div style={{ display:'flex', alignItems:'center', gridColumn:'2 / span 1' }}>
                <input type='checkbox' name='userAccountEnabled' checked={form.userAccountEnabled} onChange={handleFormChange} style={{ zoom:1.2, marginRight:8 }} />
              </div>
              <label style={{ alignSelf: 'center', fontWeight: 500, gridColumn: '1 / span 1', textAlign: 'left' }}>Domain Account</label>
             <div style={{ display: 'flex', alignItems: 'center', gridColumn: '2 / span 1' }}><input type='checkbox' name='userIsDomain' checked={form.userIsDomain} onChange={handleFormChange} style={{ zoom: 1.2 }} />
                                  <small>Check this when membership is controlled via ActiveDirectory/EntraId</small>
</div>
              {selectedUser && selectedUser.userLastLogin && (
                <>
                  <label style={{ alignSelf:'center', fontWeight:500 }}>Last Login</label>
                  <div>{formatLastLogin(selectedUser.userLastLogin)}</div>
                </>
              )}
              <div style={{ gridColumn:'1 / span 2', display:'flex', gap:12, marginTop:8, justifyContent:'space-between' }}>
                <div style={{ display:'flex', gap:12 }}>
                  <button type='submit' style={buttonPrimary} disabled={loading}>Save</button>
                  <button type='button' style={buttonSecondary} onClick={handleCancel} disabled={loading}>Cancel</button>
                </div>
                {selectedId !== null && <button type='button' style={buttonDanger} onClick={handleDelete} disabled={loading}>Delete</button>}
              </div>
            </form></div>
          )}
        </div>
      </div>
    </div>
  );
}

function isNeverLoggedIn(val:any){
  if(!val) return true;
  if(typeof val==='string') return val.startsWith('0001-01-01') || val.startsWith('0001');
  if(val instanceof Date) return val.getFullYear()===1;
  return false;
}
